/**
 * Utilities for distinguishing URL-based Learning Items from ZIP-based ones.
 *
 * A Learning Item can be either:
 *   - ZIP-based: uploaded .zip file, has a `launch_file`, content_type_id = ContentTypes.Zip.id (31)
 *   - URL-based: external URL (e.g. Moodle SCORM player), no `launch_file`, content.url starts with http(s)
 *
 * Detection is intentionally NOT based on file extension (.php, .html, etc.) because
 * LMS player URLs are often PHP scripts. Instead we test for:
 *   1. link type is Learning (id = 5)
 *   2. no launch_file present
 *   3. url is an absolute http(s) URL
 */

import { LinkTypes } from 'helpers';

/**
 * Returns true when `content` is a URL-based (non-ZIP) Learning Item.
 *
 * Works with both the flat content list shape (from SelectContentDialog) and
 * the nested content version shape (from AddContentPlugin / LinkButton).
 *
 * @param {object} content - a content record or content-version-bearing object.
 *   Expected fields (any of these paths are checked):
 *     - default_link_type_id  (content list / fetchContentById shape)
 *     - link_type_id          (link shape)
 *     - url                   the resolved content URL
 *     - launch_file           set only for ZIP items
 * @returns {boolean}
 */
export function isUrlLearningContent(content) {
    if (!content) return false;

    const linkTypeId =
        content.default_link_type_id ??
        content.link_type_id ??
        content.link_type?.link_type_id;

    if (linkTypeId !== LinkTypes.Learning.id) return false;

    // ZIP items always have a non-empty launch_file — check both the top-level field
    // and last_content_version, because after a fresh create() the API returns the
    // launch_file inside last_content_version rather than on the root object.
    const launchFile =
        content.launch_file ||
        content.last_content_version?.launch_file;

    if (launchFile) return false;

    // content_version_type 5 is the ZIP/Learning file type — treat any record with
    // that type as a ZIP item regardless of whether launch_file was populated yet.
    const cvType = content.last_content_version?.content_version_type;
    if (cvType === 5) return false;

    // Confirm the url is an absolute http(s) URL — guards against unconfigured ZIP items
    // that have no launch_file yet (we don't want to treat them as URL items).
    const url = content.url ?? content.last_content_version?.url ?? '';
    return /^https?:\/\//i.test(url);
}
